// LIBRARIES
import React from "react";
import { useT } from "@transifex/react";
import { useTestCreationContext } from "../../../../context/TestCreationContext";
import { TestQuestionModel } from "p6m-p6u";
import { Exercises, ExerciseTypes } from "../../../../enums/exercises";
import { sanitizeText } from "../../../../helpers/TestCreation";
import { getExerciseTypeByQuestionMode } from "../../../../helpers/ExerciseTypesHelper";
import { formatScoreToCommaSeparated, getScoreForExercise } from "../../../../helpers/ScoringHelper";
import { SummaryModes } from "../../../../views/create/summary/Summary";
import { SummaryOpenModals } from "../../../../views/create/summary/SummaryModals";
import ArrowIcon from "../../../../basic/arrowIcon/ArrowIcon";
import PhaseSixIcon from "../../../../basic/phaseSixIcon/PhaseSixIcon";
import useExerciseTitle from "../../../../hooks/useExerciseTitle";
import useBreakpoints from "../../../../hooks/useBreakpoints";

const DESKTOP_OVERVIEW_CLASS_NAMES =
    "mx-m flex flex-1 flex-row items-center justify-center gap-xxs overflow-hidden p-xs text-m [mask-image:linear-gradient(to_right,white_88%,transparent_100%)]";

// 3 rows fit exactly: 3 x leading-m + 2 x gap-xs, so the cut-off never slices through a line.
// The fade keeps the third row readable (white until row 2 ends, 35% white at the bottom edge).
const MOBILE_OVERVIEW_CLASS_NAMES =
    "max-h-[calc(3*var(--spacing-m)+2*var(--spacing-xs))] w-full overflow-hidden text-m leading-m [mask-image:linear-gradient(to_bottom,white_calc(2*var(--spacing-m)+var(--spacing-xs)),rgba(255,255,255,0.35)_calc(3*var(--spacing-m)+2*var(--spacing-xs)))]";

const TITLE_CLASS_NAMES = "text-l font-bold [overflow-wrap:anywhere]";

export interface ExerciseItemHeaderProps {
    isNewExercise?: boolean;
    isEditMode?: boolean;
    exerciseData: TestQuestionModel;
    index: number;
    setCurrentMode: React.Dispatch<React.SetStateAction<SummaryModes>>;
    setOpenModal: React.Dispatch<React.SetStateAction<SummaryOpenModals | undefined>>;
    saveTestDataDebounced: (newTestContent: TestQuestionModel[]) => void;
    onAction: () => void;
}

const ExerciseItemHeader: React.FC<ExerciseItemHeaderProps> = (props) => {
    const {
        testContent,
        setTestContent,
        openWarningModal,
        setCurrentExerciseModel,
        setIsTestSynced,
        showScores,
        vocabularyDrawerContent,
        setVocabularyDrawerContent,
    } = useTestCreationContext();

    const { isAboveMD } = useBreakpoints();

    const { getExerciseNameFromQuestionMode, ExerciseTypesExerciseName } = useExerciseTitle();

    const t = useT();
    const t_words = t("{wordCount} Words", {
        wordCount: props.exerciseData.selectedWords?.length,
        _tags: "CreateTest,SummaryExerciseItem",
    });
    const t_noVerbtraining = t("You cannot edit this exercise ({verbtrainingName}) in the current version.", {
        verbtrainingName: ExerciseTypesExerciseName.get(Exercises.VERBTRAINING),
        _tags: "CreateTest,SummaryExerciseItem",
    });
    const t_NewExercise = t("New exercise:", { _tags: "CreateTest,ExerciseDetails" });

    const exerciseName = getExerciseNameFromQuestionMode(props.exerciseData.questionMode);
    const exerciseTypeKey = props.exerciseData.questionMode
        ? getExerciseTypeByQuestionMode(props.exerciseData.questionMode)
        : undefined;

    const isVerbtraining = props.exerciseData.questionMode === ExerciseTypes.get(Exercises.VERBTRAINING);
    const isFreeText = props.exerciseData.questionMode === ExerciseTypes.get(Exercises.FREE_TEXT);
    const isMobileCompactView = !isAboveMD && !props.isEditMode;
    // The narrow edit view is the only case where the title can outgrow one line, so it needs a
    // dedicated layout that keeps the number + actions pinned while the title wraps.
    const isMobileEditView = !isAboveMD && props.isEditMode;

    function loadExercise(exerciseId: string) {
        const exercise = testContent.find((t) => t.exerciseId === exerciseId);
        if (exercise) {
            setCurrentExerciseModel({ ...exercise, status: "EDIT", wordsInProgress: exercise.selectedWords });
        }
    }

    function deleteExerciseFromContent(exerciseId: string): TestQuestionModel[] {
        const newTestContent = testContent.filter((t) => t.exerciseId !== exerciseId);
        const drawerContentCopy = { ...vocabularyDrawerContent };

        for (const word of Object.keys(drawerContentCopy)) {
            const wordData = drawerContentCopy[word];
            const newExercisesIdArray = wordData.exerciseIds?.filter((eId) => eId !== exerciseId);
            if (!newExercisesIdArray || newExercisesIdArray.length === 0) {
                wordData.exerciseIds = [];
                wordData.isInMoreThanOneExercise = false;
            } else {
                wordData.exerciseIds = newExercisesIdArray;
                wordData.isInMoreThanOneExercise = newExercisesIdArray.length > 1;
            }
        }

        setTestContent(newTestContent);
        setVocabularyDrawerContent(drawerContentCopy);

        return newTestContent;
    }

    function moveExercise(index: number, direction: "UP" | "DOWN") {
        if (direction === "UP" ? index < 1 : index === testContent.length - 1) return;

        const shift = direction === "UP" ? -1 : +1;
        const newTestContent = [...testContent];
        [newTestContent[index], newTestContent[index + shift]] = [newTestContent[index + shift], newTestContent[index]];

        setTestContent(newTestContent);
        setIsTestSynced(false);
        props.saveTestDataDebounced(newTestContent);
        props.onAction();
        props.setCurrentMode(SummaryModes.OVERVIEW);
    }

    function editExercise() {
        if (isVerbtraining) {
            openWarningModal(t_noVerbtraining);
            return;
        }

        props.onAction();
        loadExercise(props.exerciseData.exerciseId!);
        props.setCurrentMode(SummaryModes.EXERCISE_DETAILS);
    }

    function deleteExercise() {
        if (testContent.findIndex((e) => e.exerciseId === props.exerciseData.exerciseId) === -1) {
            setIsTestSynced(true);
            props.onAction();
            props.setCurrentMode(SummaryModes.OVERVIEW);
        } else if (testContent.length > 1) {
            const newTestContent = deleteExerciseFromContent(props.exerciseData.exerciseId!);
            setIsTestSynced(false);
            props.saveTestDataDebounced(newTestContent);
            props.onAction();
            props.setCurrentMode(SummaryModes.OVERVIEW);
        } else {
            props.setOpenModal(SummaryOpenModals.DELETION_FORBIDDEN);
        }
    }

    const renderNumber = () => (
        <div className="flex size-xl shrink-0 items-center justify-around rounded-xsmall-container bg-background-light p-s text-s">
            {props.index + 1}
        </div>
    );

    const renderTitle = (additionalClassNames: string) => (
        <div className={`flex min-w-0 flex-row flex-wrap gap-xxs ${additionalClassNames}`}>
            {props.isNewExercise && <span className={TITLE_CLASS_NAMES}>{t_NewExercise}</span>}
            <span className={TITLE_CLASS_NAMES}>{exerciseName}</span>
        </div>
    );

    const renderWords = () => {
        const words =
            props.exerciseData.selectedWords?.map((w) => ("answer" in w ? sanitizeText(w.answer || "") : "")) ?? [];
        const separatorClassNames = isMobileCompactView ? "flex" : "flex text-xl font-bold";
        return words.flatMap((w, i) =>
            i === 0 ? (
                <span
                    key={`word_${i}`}
                    className="whitespace-nowrap"
                >
                    {w}
                </span>
            ) : (
                [
                    <span
                        key={`word_separator_${i}`}
                        className={separatorClassNames}
                    >
                        {"·"}
                    </span>,
                    <span
                        key={`word_${i}`}
                        className="whitespace-nowrap"
                    >
                        {w}
                    </span>,
                ]
            )
        );
    };

    const renderDescription = () => (
        <div className={DESKTOP_OVERVIEW_CLASS_NAMES}>
            <div className="flex flex-row items-center gap-xxs overflow-hidden">
                <span className="whitespace-nowrap">{props.exerciseData.extraComments}</span>
            </div>
        </div>
    );

    const renderVocabularyOverview = () => (
        <div className={DESKTOP_OVERVIEW_CLASS_NAMES}>
            <span className="flex whitespace-nowrap">{t_words}</span>
            <span className="text-xl">{" | "}</span>
            <div className="flex flex-row items-center gap-xxs overflow-hidden">{renderWords()}</div>
        </div>
    );

    const renderMobileVocabularyOverview = () => (
        <div className={`flex flex-wrap items-center gap-xs ${MOBILE_OVERVIEW_CLASS_NAMES}`}>
            <span className="border-r border-p6-grey-dark pr-xs font-semibold whitespace-nowrap">{t_words}</span>
            {renderWords()}
        </div>
    );

    const renderMobileScore = () =>
        showScores && (
            <span className="flex min-h-xl items-center whitespace-nowrap text-m font-semibold">
                {`${formatScoreToCommaSeparated(getScoreForExercise(props.exerciseData))} P.`}
            </span>
        );

    const renderEditAction = () =>
        !props.isEditMode && (
            <div
                className="cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    editExercise();
                }}
            >
                <PhaseSixIcon
                    className="w-l text-xxl"
                    name={"edit-avatar"}
                />
            </div>
        );

    const renderDeleteAction = () => (
        <div
            className="cursor-pointer"
            data-testid={`exercise-item-delete-${props.index}`}
            onClick={(e) => {
                e.stopPropagation();
                deleteExercise();
            }}
        >
            <PhaseSixIcon
                className="w-l text-xxl"
                name={"trash"}
            />
        </div>
    );

    const renderArrowActions = () =>
        !props.isEditMode && (
            <div className="flex flex-col items-center">
                <ArrowIcon
                    onClick={(e) => {
                        e.stopPropagation();
                        moveExercise(props.index, "UP");
                    }}
                    disabled={props.index === 0}
                    direction={"TOP"}
                />
                <ArrowIcon
                    onClick={(e) => {
                        e.stopPropagation();
                        moveExercise(props.index, "DOWN");
                    }}
                    disabled={props.index === testContent.length - 1}
                    direction={"BOTTOM"}
                />
            </div>
        );

    const renderActions = () =>
        isMobileCompactView ? (
            <div className="flex w-full items-center justify-end gap-l">
                <div className="flex items-center gap-s">
                    {renderEditAction()}
                    {renderDeleteAction()}
                </div>
                {renderArrowActions()}
            </div>
        ) : (
            <div className="flex items-center justify-end gap-s">
                {renderEditAction()}
                {renderDeleteAction()}
                {renderArrowActions()}
            </div>
        );

    // Shared number + title row for both the mobile card and its edit view. The title gets a
    // badge-height min-height with centred content so a one-line title sits level with the number and
    // the trailing element, while a wrapped title grows and keeps both of them pinned to the top.
    const renderMobileNumberTitleRow = (trailing: React.ReactNode) => (
        <div className="flex w-full flex-row items-start gap-s">
            {renderNumber()}
            {renderTitle("flex-1 min-h-xl content-center")}
            {trailing}
        </div>
    );

    const renderMobileHeaderContent = () => (
        <>
            {renderMobileNumberTitleRow(renderMobileScore())}
            {!isFreeText && renderMobileVocabularyOverview()}
            {renderActions()}
        </>
    );

    const renderMobileEditHeaderContent = () =>
        renderMobileNumberTitleRow(<div className="flex min-h-xl items-center">{renderActions()}</div>);

    const renderHeaderContent = () => (
        <>
            {renderNumber()}
            <div className="flex flex-1 shrink-0 flex-row items-center justify-between overflow-hidden">
                {renderTitle("ml-s")}
                {!props.isEditMode && (isFreeText ? renderDescription() : renderVocabularyOverview())}
                {renderActions()}
            </div>
        </>
    );

    const layoutClassNames = isMobileCompactView
        ? "flex flex-col gap-s p-s cursor-pointer"
        : isMobileEditView
        ? "flex flex-col gap-s p-s"
        : `flex h-xxl flex-1 flex-row items-center px-s py-l ${!props.isEditMode ? "cursor-pointer" : ""}`;

    return (
        <div
            data-testid={`exercise-item-header${exerciseTypeKey ? `-${exerciseTypeKey}` : ""}`}
            className={`select-none animate-fade-in ${layoutClassNames} ${isVerbtraining ? "bg-transparent-grey" : ""}`}
            onClick={props.isEditMode ? undefined : () => editExercise()}
        >
            {isMobileCompactView
                ? renderMobileHeaderContent()
                : isMobileEditView
                ? renderMobileEditHeaderContent()
                : renderHeaderContent()}
        </div>
    );
};

export default ExerciseItemHeader;
